/**
 * Pipeline stage filter. Tabs are driven by the job's pipeline stages, so the
 * name, order, and set of tabs always match the Kanban columns.
 *
 * Selection chrome is SegmentedButton; counts and GTM stay here as consumer
 * composition. Stages that do not fit the available width move into a "More"
 * menu (see useTabOverflow); the active stage always stays a visible tab.
 */

import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createGTMTracking, getGTMAttributes } from '@/utils/gtm/gtm-tracking';
import { SegmentedButton } from '@/components/ui/atoms/SegmentedButton';
import type { SegmentedButtonOption } from '@/components/ui/atoms/SegmentedButton';
import { useTabOverflow } from '@/hooks/useTabOverflow';
import { StatusTabCount } from './StatusTabCount';
import { StatusTabsMore } from './StatusTabsMore';
import { STATUS_TABS_SEGMENT, STATUS_TABS_TRACK_CLASS } from './statusTabs.config';

export interface StatusTabItem {
  /** Pipeline stage id. */
  id: string;
  label: string;
  /** Count for the same candidate set the view is rendering (e.g. search-filtered). */
  count: number;
  /** Stage semantic for analytics — stage ids are opaque and not page-agnostic. */
  trackingTarget: string;
}

export interface StatusTabsProps {
  tabs: StatusTabItem[];
  activeId: string;
  onChange: (stageId: string) => void;
  /** Rendered right after the tabs, never moved into "More" (e.g. create stage). */
  trailing?: React.ReactNode;
  className?: string;
}

const noop = () => {};

export const StatusTabs: React.FC<StatusTabsProps> = React.memo(({
  tabs,
  activeId,
  onChange,
  trailing,
  className = '',
}) => {
  const { t, i18n } = useTranslation('job');
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const handleChange = useCallback((id: string) => {
    onChangeRef.current(id);
  }, []);

  const trackingById = useMemo(
    () =>
      Object.fromEntries(
        tabs.map((item) => [
          item.id,
          getGTMAttributes(
            createGTMTracking('NAVIGATE_CANDIDATE_STATUS', {
              origin: 'status_tabs',
              target: item.trackingTarget,
            }),
          ),
        ]),
      ),
    [tabs],
  );

  const measureKey = useMemo(
    () =>
      [i18n.language, ...tabs.map((tab) => `${tab.id}:${tab.label}:${tab.count}`)].join('|'),
    [i18n.language, tabs],
  );

  const { containerRef, measureRef, trailingRef, visibleIndices, overflowIndices } = useTabOverflow({
    tabCount: tabs.length,
    activeIndex: tabs.findIndex((tab) => tab.id === activeId),
    measureKey,
  });

  const toOption = (item: StatusTabItem, isTracked: boolean): SegmentedButtonOption => ({
    value: item.id,
    label: (
      <span className="inline-flex items-center gap-1.5">
        <span>{item.label}</span>
        <StatusTabCount count={item.count} isActive={activeId === item.id} />
      </span>
    ),
    dataAttributes: isTracked ? trackingById[item.id] : undefined,
  });

  const ariaLabel = t('status_tabs.aria_label', {
    defaultValue: 'Filter candidates by hiring stage',
  });
  // Segments sit in STATUS_TABS_TRACK_CLASS next to "More" (track={false}).
  const segmentedProps = {
    ...STATUS_TABS_SEGMENT,
    track: false,
    value: activeId,
    className,
  } as const;
  const overflowTabs = overflowIndices.map((index) => tabs[index]);

  return (
    <div ref={containerRef} className="relative flex w-full min-w-0 items-center gap-2">
      <div className={STATUS_TABS_TRACK_CLASS}>
        <SegmentedButton
          {...segmentedProps}
          aria-label={ariaLabel}
          onValueChange={handleChange}
          options={visibleIndices.map((index) => toOption(tabs[index], true))}
        />
        {overflowTabs.length > 0 ? (
          <StatusTabsMore
            tabs={overflowTabs}
            activeId={activeId}
            onSelect={handleChange}
            trackingById={trackingById}
          />
        ) : null}
      </div>
      {trailing ? (
        <div ref={trailingRef} className="shrink-0">
          {trailing}
        </div>
      ) : null}
      {/* Measuring mirror: every tab + "More", never visible or interactive. */}
      <div
        aria-hidden
        inert
        className="pointer-events-none invisible absolute left-0 top-0 h-0 w-0 overflow-hidden"
      >
        <div ref={measureRef} className="w-max">
          <div data-tab-overflow="track" className={STATUS_TABS_TRACK_CLASS}>
            <SegmentedButton
              {...segmentedProps}
              aria-label={ariaLabel}
              onValueChange={noop}
              options={tabs.map((item) => toOption(item, false))}
            />
            <div data-tab-overflow="more">
              <StatusTabsMore tabs={[]} activeId={activeId} onSelect={noop} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

StatusTabs.displayName = 'StatusTabs';

export default StatusTabs;
